/** Base role ids as they appear in Account.baseRoleIds. */
export const BaseRole = {
    CORPORATE_USER: 'CORPORATE_USER',
    DSQM: 'DSQM',
    LAG_SUPER_ADMIN: 'LAG_SUPER_ADMIN',
    NASI_SIA_ADMIN: 'NASI_SIA_ADMIN',
    RETAILER_ADMIN: 'RETAILER_ADMIN',
    RETAILER_USER: 'RETAILER_USER',
} as const;

/** One of the {@link BaseRole} ids. */
export type BaseRoleId = (typeof BaseRole)[keyof typeof BaseRole];

/** An account's base role ids, as sent by the backend. */
type RoleIds = readonly string[] | undefined;

/**
 * Whether the account holds at least one of the given roles.
 *
 * @param baseRoleIds - The account's base role ids.
 * @param roles - Roles to test for.
 * @returns true when at least one role matches.
 */
export function hasAnyRole(baseRoleIds: RoleIds, roles: readonly BaseRoleId[]): boolean {
    return (baseRoleIds ?? []).some((id) => (roles as readonly string[]).includes(id));
}

/**
 * Whether the account is corporate-only. Retailer accounts also carry
 * CORPORATE_USER, so this must exclude retailers.
 *
 * @param baseRoleIds - The account's base role ids.
 * @returns true when the account holds CORPORATE_USER and no retailer role.
 */
export function isCorporateOnlyAccount(baseRoleIds: RoleIds): boolean {
    return hasAnyRole(baseRoleIds, [BaseRole.CORPORATE_USER]) && !isRetailerAccount(baseRoleIds);
}

/**
 * Whether the account is a DSQM reviewer.
 *
 * @param baseRoleIds - The account's base role ids.
 * @returns true when the account holds the DSQM role.
 */
export function isDsqmAccount(baseRoleIds: RoleIds): boolean {
    return hasAnyRole(baseRoleIds, [BaseRole.DSQM]);
}

/**
 * Whether the account is a retailer-side account.
 *
 * @param baseRoleIds - The account's base role ids.
 * @returns true for retailer users and retailer admins.
 */
export function isRetailerAccount(baseRoleIds: RoleIds): boolean {
    return hasAnyRole(baseRoleIds, [BaseRole.RETAILER_USER, BaseRole.RETAILER_ADMIN]);
}
